import api from '@/lib/axios';
import { downloadExport, PromotionResult, PromotionStatus } from '@/services/school.api';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export type BulkPromotionAction = 'promote' | 'leave';

export interface BulkPromotePayload {
  schoolIds: string[];
  fromClassName: string;
  toClassName?: string;
  fromAcademicYearName: string;
  toAcademicYearName?: string;
  action: BulkPromotionAction;
}

export interface BulkPromoteSchoolResult {
  schoolId: string;
  schoolName: string;
  status: 'success' | 'failed';
  reason?: string;
  processed?: number;
  promoted?: number;
  repeating?: number;
  left?: number;
}

export interface PromotionLogRow {
  id: string;
  action: 'promote' | 'repeat' | 'leave';
  createdAt: string;
  schoolId: string;
  schoolName: string;
  studentId: string;
  studentName: string;
  admissionNumber: string;
  fromClassName: string;
  fromSectionName: string;
  toClassName: string | null;
  toSectionName: string | null;
  fromAcademicYearName: string;
  toAcademicYearName: string | null;
  actorName: string | null;
}

export interface ListPromotionLogParams {
  schoolId?: string;
  academicYearName?: string;
  className?: string;
}

export interface BulkPromotionOptions {
  classNames: string[];
  academicYearNames: string[];
}

export interface OrgPromoteStudentClassSection {
  id: string;
  name: string;
}

export interface OrgPromoteStudentClass {
  id: string;
  name: string;
  sections: OrgPromoteStudentClassSection[];
}

export interface OrgPromoteStudentAcademicYear {
  id: string;
  name: string;
  isCurrent: boolean;
}

export interface OrgPromoteStudentRosterRow {
  id: string;
  admissionNo: string;
  name: string | null;
  rollNumber: number | null;
}

export interface OrgPromoteStudentPayload {
  schoolId: string;
  fromSectionId: string;
  toSectionId: string;
  newAcademicYearId: string;
  students: { studentId: string; result: PromotionResult; status: PromotionStatus }[];
}

export interface OrgPromoteStudentResult {
  processed: number;
  students: {
    studentId: string;
    admissionNo: string;
    outcome: 'promoted' | 'repeating' | 'left';
    enrollmentId: string | null;
    rollNumber: number | null;
  }[];
}

/** Org-tier Promotion — Promote Student (single school, School picked inline), Bulk Promotion (new orchestration across schools), and the Promotion Report (reads promotion_log). */
export const OrgPromotionApi = {
  classesForSchool: async (schoolId: string): Promise<OrgPromoteStudentClass[]> =>
    unwrap(await api.get('/org/promote-student/classes', { params: { schoolId } })),

  academicYearsForSchool: async (schoolId: string): Promise<OrgPromoteStudentAcademicYear[]> =>
    unwrap(await api.get('/org/promote-student/academic-years', { params: { schoolId } })),

  studentsForSection: async (schoolId: string, sectionId: string): Promise<OrgPromoteStudentRosterRow[]> =>
    unwrap(await api.get('/org/promote-student/students', { params: { schoolId, sectionId } })),

  promoteStudent: async (payload: OrgPromoteStudentPayload): Promise<OrgPromoteStudentResult> =>
    unwrap(await api.post('/org/promote-student', payload)),

  bulkPromotionOptions: async (schoolIds?: string[]): Promise<BulkPromotionOptions> =>
    unwrap(await api.get('/org/bulk-promotion/options', { params: schoolIds?.length ? { schoolIds } : {} })),

  bulkPromote: async (payload: BulkPromotePayload): Promise<{ schools: BulkPromoteSchoolResult[] }> =>
    unwrap(await api.post('/org/students/bulk-promote', payload)),

  listLog: async (params?: ListPromotionLogParams): Promise<{ rows: PromotionLogRow[]; truncated: boolean }> =>
    unwrap(await api.get('/org/promotion-log', { params })),

  exportPdf: (params?: ListPromotionLogParams) =>
    downloadExport('/org/promotion-log/pdf', 'promotion-report', 'pdf', params as Record<string, unknown>),
};
